/**
 * KitchenCockpit — Tableau de service du cuisinier (mode simple, Juin 2026)
 *
 * Une carte par table : numéro en gros, chrono, liste des plats en gros caractères,
 * un gros bouton « PRÊT » par plat et un bouton « TOUT EST PRÊT » en bas.
 * Aucune statistique ici : le cuisinier ne voit que ses bons.
 */
import React, { useState, useEffect, useMemo } from "react";
import { Loader2, ChefHat, CheckCircle2, Timer, Users, Check } from "lucide-react";

const SLA_FRESH = 5 * 60;
const SLA_ATTENTION = 15 * 60;

const fmtElapsed = (s) => {
  const m = Math.floor(s / 60);
  if (m < 1) return "< 1 min";
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, "0")}`;
};

const getSlaState = (createdAt, isReady) => {
  if (isReady) return "ready";
  if (!createdAt) return "fresh";
  const elapsed = Math.max(0, (Date.now() - new Date(createdAt).getTime()) / 1000);
  if (elapsed < SLA_FRESH) return "fresh";
  if (elapsed < SLA_ATTENTION) return "attention";
  return "urgent";
};

const SLA_STYLES = {
  fresh:     { card: "bg-slate-900 border-emerald-500/50", head: "bg-emerald-500 text-slate-950", label: "NOUVEAU" },
  attention: { card: "bg-slate-900 border-amber-500/70", head: "bg-amber-500 text-slate-950", label: "EN COURS" },
  urgent:    { card: "bg-rose-950/40 border-rose-500 ring-4 ring-rose-500/40 animate-pulse", head: "bg-rose-500 text-white", label: "EN RETARD" },
  ready:     { card: "bg-slate-900/60 border-cyan-500/50 opacity-80", head: "bg-cyan-500 text-slate-950", label: "PRÊT À SERVIR" },
};

const useNow = (intervalMs = 1000) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
};

const TableCard = ({ order, now, onMarkItem, onMarkAllReady }) => {
  const [busy, setBusy] = useState(false);
  const allReady = order.all_ready;
  const elapsed = order.created_at ? Math.max(0, Math.floor((now - new Date(order.created_at).getTime()) / 1000)) : 0;
  const slaState = getSlaState(order.created_at, allReady);
  const style = SLA_STYLES[slaState];
  const items = order.items || [];
  const readyCount = items.filter((i) => i.ready).length;

  const markAll = async () => {
    setBusy(true);
    try { await onMarkAllReady(order.id); } finally { setBusy(false); }
  };

  return (
    <div
      className={`rounded-3xl border-2 overflow-hidden ${style.card}`}
      data-testid={`table-bubble-${order.id}`}
      data-sla-state={slaState}
    >
      <div className={`flex items-center justify-between px-5 py-3 ${style.head}`}>
        <div className="flex items-baseline gap-2">
          <span className="text-xs font-black uppercase tracking-widest opacity-80">Table</span>
          <span className="text-4xl font-black leading-none tracking-tight" data-testid={`table-number-${order.id}`}>{order.table_number}</span>
        </div>
        <div className="text-right">
          <div className="flex items-center justify-end gap-1 text-2xl font-black font-mono leading-none">
            <Timer className="w-5 h-5" strokeWidth={2.5} />
            {fmtElapsed(elapsed)}
          </div>
          <div className="text-[10px] font-black uppercase tracking-widest mt-0.5 opacity-90">{style.label}</div>
        </div>
      </div>

      {(order.server_name || order.client_name) && (
        <div className="px-5 pt-3 flex items-center gap-2 text-sm text-slate-400">
          <Users className="w-4 h-4" />
          <span className="truncate">{order.server_name}{order.client_name ? ` · ${order.client_name}` : ""}</span>
        </div>
      )}

      <div className="p-4 space-y-2">
        {items.map((it) => (
          <button
            key={it.index}
            type="button"
            disabled={it.ready}
            onClick={() => onMarkItem(order.id, it.index)}
            className={`w-full flex items-center gap-3 rounded-2xl px-4 py-3 text-left border-2 transition-all active:scale-[0.98] ${
              it.ready
                ? "bg-emerald-500/15 border-emerald-500/40 text-slate-500"
                : "bg-slate-800 border-slate-600 hover:border-emerald-400 hover:bg-slate-700 text-white"
            }`}
            data-testid={`bubble-item-check-${order.id}-${it.index}`}
          >
            <span className={`text-3xl font-black font-mono w-14 flex-shrink-0 ${it.ready ? "text-emerald-400" : "text-amber-300"}`}>
              ×{it.quantity}
            </span>
            <span className={`flex-1 text-xl sm:text-2xl font-bold leading-tight ${it.ready ? "line-through" : ""}`}>{it.name}</span>
            <span className={`flex-shrink-0 rounded-xl px-3 py-2 text-sm font-black flex items-center gap-1 ${
              it.ready ? "bg-emerald-500 text-slate-950" : "bg-slate-700 text-slate-200 border border-slate-500"
            }`}>
              <Check className="w-5 h-5" strokeWidth={3} />
              {it.ready ? "PRÊT" : "PRÊT ?"}
            </span>
          </button>
        ))}
      </div>

      <div className="px-4 pb-4">
        {allReady ? (
          <div className="w-full rounded-2xl bg-cyan-500/20 border-2 border-cyan-500/50 text-cyan-100 py-4 text-center text-lg font-black flex items-center justify-center gap-2">
            <CheckCircle2 className="w-6 h-6" /> PRÊT À SERVIR
          </div>
        ) : (
          <button
            type="button"
            onClick={markAll}
            disabled={busy}
            className="w-full rounded-2xl bg-emerald-500 hover:bg-emerald-400 active:scale-[0.98] text-slate-950 py-5 text-xl font-black flex items-center justify-center gap-2 transition-all disabled:opacity-60"
            data-testid={`complete-all-${order.id}`}
          >
            {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <CheckCircle2 className="w-7 h-7" strokeWidth={2.5} />}
            TOUT EST PRÊT
            <span className="text-sm font-bold opacity-70 ml-1">({readyCount}/{items.length})</span>
          </button>
        )}
      </div>
    </div>
  );
};

const KitchenCockpit = ({ orders = [], loading, onMarkItem, onMarkAllReady }) => {
  const now = useNow(1000);

  const sortedOrders = useMemo(() => {
    const rank = { urgent: 0, attention: 1, fresh: 2, ready: 3 };
    return [...orders].sort((a, b) => {
      const sa = getSlaState(a.created_at, a.all_ready);
      const sb = getSlaState(b.created_at, b.all_ready);
      if (rank[sa] !== rank[sb]) return rank[sa] - rank[sb];
      return new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
    });
  }, [orders]);

  return (
    <div className="space-y-4" data-testid="kitchen-cockpit">
      {orders.length === 0 && !loading && (
        <div className="text-center py-16 px-4" data-testid="cockpit-empty">
          <div className="mx-auto w-28 h-28 mb-5 rounded-full bg-slate-900/80 ring-2 ring-amber-500/20 flex items-center justify-center">
            <ChefHat className="w-14 h-14 text-amber-400/70" />
          </div>
          <h3 className="text-3xl font-black text-slate-200 tracking-tight">Aucun bon en cuisine</h3>
          <p className="text-slate-500 text-base mt-2">Les nouvelles commandes apparaîtront ici automatiquement.</p>
        </div>
      )}

      {orders.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {sortedOrders.map((o) => (
            <TableCard key={o.id} order={o} now={now} onMarkItem={onMarkItem} onMarkAllReady={onMarkAllReady} />
          ))}
        </div>
      )}
    </div>
  );
};

export default KitchenCockpit;
